<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import type { TAssistantStatus } from '@/composition/assistant/use-assistant-store';
  import { isAssistantRtl } from '@/domain/assistant/assistant-direction';
  import type { TAssistantTier } from '@/domain/assistant/assistant-panel-tier';
  import { ASSISTANT_GA } from '@/domain/assistant/assistant-rollout';

  /**
   * The panel before the widget is mounted (plan §9.2): its own header (a
   * copy of the widget's, which does not exist yet) over the loading,
   * error or unsupported-browser state, so the panel looks the same before
   * and after the widget loads. Arabic and Urdu mirror it, like the widget.
   */
  const props = withDefaults(
    defineProps<{
      status: TAssistantStatus;
      tier: TAssistantTier;
      locale: string;
      scopeLabel?: string;
    }>(),
    { scopeLabel: undefined }
  );

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'retry'): void;
  }>();

  /** Suggestion chips of the welcome block the skeleton stands in for. */
  const SKELETON_CHIPS = [184, 236, 212, 200];

  const dir = computed(() => (isAssistantRtl(props.locale) ? 'rtl' : 'ltr'));
</script>

<template>
  <div class="v2-assistant-panel-state">
    <header class="state-head" :dir="dir" :lang="locale">
      <div class="head-title">
        <div class="title-row">
          <PosUiIcon name="creation" :size="18" class="title-icon" />
          <h2 class="title-text">{{ $t('assistant.panel.label') }}</h2>
          <span v-if="!ASSISTANT_GA" class="title-badge">
            {{ $t('assistant.panel.beta') }}
          </span>
        </div>
        <p v-if="scopeLabel" class="title-scope">{{ scopeLabel }}</p>
      </div>
      <button
        v-if="tier === 'sheet'"
        type="button"
        class="head-close-text"
        @click="emit('close')"
      >
        {{ $t('assistant.panel.close') }}
      </button>
      <button
        v-else
        type="button"
        class="head-close"
        :aria-label="$t('assistant.panel.close')"
        :title="$t('assistant.panel.close')"
        @click="emit('close')"
      >
        <PosUiIcon name="close" :size="21" />
      </button>
    </header>

    <!-- One live region for every state, so a failed load is announced. -->
    <div
      class="state-body"
      role="status"
      aria-live="polite"
      :dir="dir"
      :lang="locale"
    >
      <div v-if="status === 'error'" class="state-message">
        <PosUiEmptyState
          icon="alert-circle-outline"
          :title="$t('assistant.panel.error')"
        >
          <template #action>
            <PosUiButton
              color="neutral"
              variant="outline"
              icon="refresh"
              @click="emit('retry')"
            >
              {{ $t('assistant.panel.retry') }}
            </PosUiButton>
          </template>
        </PosUiEmptyState>
      </div>
      <div v-else-if="status === 'unsupported'" class="state-message">
        <PosUiEmptyState
          icon="update"
          :title="$t('assistant.panel.unsupported')"
        />
      </div>
      <div v-else class="state-loading">
        <span class="loading-label">{{ $t('assistant.panel.loading') }}</span>
        <PosUiSkeleton class="skeleton-mark" />
        <PosUiSkeleton class="skeleton-title" />
        <PosUiSkeleton class="skeleton-line" />
        <PosUiSkeleton class="skeleton-line" />
        <PosUiSkeleton class="skeleton-line is-short" />
        <PosUiSkeleton
          v-for="width in SKELETON_CHIPS"
          :key="width"
          class="skeleton-chip"
          :style="{ width: `${width}px` }"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-assistant-panel-state {
    @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col;

    // The widget header's sizes; logical paddings and margins only, so the
    // `dir="rtl"` header mirrors (Tailwind keeps `pl`/`ml`/`space-x` physical).
    .state-head {
      @apply tw-flex tw-shrink-0 tw-items-center tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-py-2 tw-pe-1.5 tw-ps-4;
      min-height: 60px;
    }

    .head-title {
      @apply tw-min-w-0 tw-flex-1;
    }

    .title-row {
      @apply tw-flex tw-items-center;
    }

    .title-icon {
      @apply tw-me-1.5 tw-text-neutral-800;
    }

    .title-text {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-leading-tight tw-text-neutral-900;
    }

    .title-badge {
      @apply tw-ms-2 tw-rounded-lg tw-bg-neutral-100 tw-px-1.5 tw-font-medium tw-uppercase tw-text-neutral-600;
      font-size: 10.5px;
      letter-spacing: 0.04em;
      line-height: 1.5;

      // Spacing cuts Arabic joins and the Devanagari/Bengali headstroke
      // (like the widget's header; separate rules, not a list).
      &:lang(ar) {
        letter-spacing: normal;
      }

      &:lang(ur) {
        letter-spacing: normal;
      }

      &:lang(hi) {
        letter-spacing: normal;
      }

      &:lang(bn) {
        letter-spacing: normal;
      }
    }

    .title-scope {
      @apply tw-m-0 tw-truncate tw-text-neutral-500;
      font-size: 12.5px;
    }

    .head-close {
      @apply tw-ms-1 tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-600;

      &:hover {
        @apply tw-bg-neutral-100 tw-text-neutral-900;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .head-close-text {
      @apply tw-ms-1 tw-h-11 tw-shrink-0 tw-cursor-pointer tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-700;

      &:hover {
        @apply tw-bg-neutral-100 tw-text-neutral-900;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .state-body {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto;
    }

    .state-message {
      @apply tw-flex tw-h-full tw-items-center tw-justify-center;
    }

    // The welcome block's shape: mark, title, three lines, four chips.
    .state-loading {
      @apply tw-flex tw-flex-col tw-items-start tw-px-4 tw-py-6;

      .loading-label {
        @apply tw-sr-only;
      }

      .skeleton-mark {
        @apply tw-h-10 tw-w-10 tw-rounded-xl;
      }

      .skeleton-title {
        @apply tw-mt-3 tw-h-6 tw-w-48;
      }

      .skeleton-line {
        @apply tw-mt-2 tw-h-4 tw-w-full;

        &.is-short {
          @apply tw-w-2/3;
        }
      }

      .skeleton-chip {
        @apply tw-mt-3 tw-h-10 tw-max-w-full tw-rounded-full;
      }

      .skeleton-line + .skeleton-chip {
        @apply tw-mt-8;
      }
    }
  }
</style>
